import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import PageCTA from "@/components/PageCTA";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/lib/useContent";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";

export default function Subscription() {
    const { user, refresh } = useAuth();
    const { data: copy } = useContent("subscription_copy", {
        overline: "Subscribe",
        title: "£1.89 a month.",
        lede: "",
        benefits: [],
        fine_print: "",
    });

    const benefits = copy.benefits && copy.benefits.length > 0 ? copy.benefits : [
        "All behind-the-scenes flight reels",
        "RAW LOG downloads of monthly featured clips",
        "Early access to new films (one week before public)",
        "Subscriber-only post-production breakdowns",
        "Submit your own footage to the community wall",
        "Cancel anytime — no contracts",
    ];

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="subscription-page">
            <div className="text-center mb-20">
                <Reveal>
                    <div className="overline mb-5"><span className="editorial-rule" />{copy.overline}</div>
                </Reveal>
                <h1
                    className="font-serif font-light tracking-tighter leading-[0.9]"
                    style={{ fontSize: "clamp(3rem, 8vw, 8rem)" }}
                    data-testid="subscription-title"
                >
                    <div>
                        <MaskedLine delay={0.1}>
                            <span className="italic text-[var(--sb-brand)]">{copy.title}</span>
                        </MaskedLine>
                    </div>
                </h1>
                <Reveal delay={0.35}>
                    <p className="text-[var(--sb-text-dim)] mt-8 max-w-xl mx-auto text-lg leading-relaxed">
                        {copy.lede}
                    </p>
                </Reveal>
            </div>

            <Reveal delay={0.15}>
                <div className="max-w-2xl mx-auto card p-10 lg:p-14 relative overflow-hidden" data-testid="subscription-card">
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--sb-brand)] to-transparent" />
                <div className="overline mb-3">DTScotslad Monthly</div>
                <div className="flex items-end gap-3 mb-2">
                    <span className="font-serif text-7xl lg:text-8xl leading-none tracking-tighter">£1.89</span>
                    <span className="text-[var(--sb-text-dim)] mb-3">/ month</span>
                </div>
                <p className="text-[var(--sb-text-dim)] mb-8">Behind-the-scenes reels, RAW LOG downloads, and early access cuts.</p>

                <ul className="space-y-3 mb-10" data-testid="subscription-benefits">
                    {benefits.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-[var(--sb-text)]">
                            <Check size={18} className="text-[var(--sb-brand)] mt-1 shrink-0" />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>

                {user?.is_subscribed ? (
                    <div className="border border-[var(--sb-brand)] p-4 text-center text-[var(--sb-text)]" data-testid="already-subscribed">
                        You&rsquo;re already a subscriber. Thank you.
                    </div>
                ) : !user ? (
                    <div className="relative border-2 border-[var(--sb-brand)] p-5 mb-5 text-center" style={{background: "linear-gradient(135deg, rgba(138,123,102,0.08) 0%, rgba(138,123,102,0.02) 100%)"}} data-testid="signup-required">
                        <div className="absolute -top-3 left-4 bg-[var(--sb-surface)] px-3">
                            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[var(--sb-brand)]">Sign up first</span>
                        </div>
                        <p className="text-sm text-[var(--sb-text)] leading-relaxed mt-2 mb-4">
                            Please register with your email and a password before subscribing — that&rsquo;s the account we&rsquo;ll unlock the moment your payment lands.
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <Link to="/register?next=/subscription" className="btn-primary" data-testid="signup-link">
                                Sign up / Register →
                            </Link>
                            <Link to="/login?next=/subscription" className="btn-outline" data-testid="signup-signin-link">
                                Already have an account? Sign in
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        <PayPalSubscribeButton onGranted={() => { refresh?.(); }} />
                        <p className="text-xs text-[var(--sb-text-muted)] mt-4 text-center leading-relaxed">
                            Subscribing charges £1.89 today and every month after. You can cancel any time from your PayPal account or by emailing <a className="underline hover:text-[var(--sb-text)]" href="mailto:hello@dtscotslad.com">hello@dtscotslad.com</a>.
                        </p>
                    </>
                )}
                <p className="text-xs text-[var(--sb-text-muted)] mt-6 text-center">
                    Secure payment via PayPal.
                </p>
            </div>
            </Reveal>

            <div className="mt-20 max-w-2xl mx-auto text-center text-[var(--sb-text-dim)] text-sm">
                <p>{copy.fine_print}</p>
            </div>
            <PageCTA variant="flight" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
